import { Link } from 'react-router-dom';
import useScrollReveal from '@/hooks/useScrollReveal';

const reasons = [
  {
    icon: 'ri-focus-3-line',
    title: 'Individualized Training Plans',
    desc: 'Every participant receives a customized development path built around their specific goals, barriers, and strengths — not a generic classroom curriculum.',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Employment-Focused Outcomes',
    desc: 'Everything we do points toward one result: a real job. Our programs are designed around employer expectations and workforce demand, not just completion certificates.',
  },
  {
    icon: 'ri-trophy-line',
    title: '85%+ Job Placement Rate',
    desc: 'Our placement outcomes speak for themselves. Over 85% of program graduates secure employment within 90 days of completing their training track.',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Life Skills Integration',
    desc: 'We go beyond technical training. Financial literacy, professional communication, and self-management skills are woven into every program for lasting independence.',
  },
  {
    icon: 'ri-community-line',
    title: 'Community-Centered Mission',
    desc: 'OST-C was built to serve real communities facing real barriers. Our approach is rooted in empathy, inclusivity, and genuine investment in participant success.',
  },
];

const WhyChooseUs = () => {
  const headerRef = useScrollReveal();
  const gridRef = useScrollReveal();
  const imageRef = useScrollReveal();

  return (
    <section className="bg-white py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left — content */}
          <div>
            <div ref={headerRef} className="reveal">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-[2px] bg-gold" />
                <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">
                  Why Choose OST-C
                </span>
              </div>
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-4 leading-tight">
                A Higher Standard in Vocational Training
              </h2>
              <p className="font-inter text-base text-charcoal/60 leading-relaxed mb-10">
                We don&apos;t just teach skills — we build futures. Every program is designed to produce measurable, life-changing employment outcomes.
              </p>
            </div>
            <div ref={gridRef} className="reveal flex flex-col gap-6">
              {reasons.map((item) => (
                <div key={item.title} className="flex gap-5 group">
                  <div className="w-11 h-11 flex items-center justify-center bg-gold/10 rounded-lg shrink-0 group-hover:bg-gold/20 transition-colors duration-200">
                    <i className={`${item.icon} text-gold text-lg`} />
                  </div>
                  <div>
                    <h3 className="font-inter text-base font-semibold text-charcoal mb-1">{item.title}</h3>
                    <p className="font-inter text-sm text-charcoal/60 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Start Your Training Journey
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>

          {/* Right — image */}
          <div ref={imageRef} className="reveal-right sticky top-24">
            <div className="relative rounded-lg overflow-hidden">
              <div className="w-full h-[520px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/5c3df94537fbca8301373e777769ca74.jpg"
                  alt="OST-C vocational training success - Arlington TX"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 to-transparent" />
              </div>
              {/* Stats overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-7">
                <div className="bg-charcoal/90 backdrop-blur-sm rounded-lg p-5 border border-gold/20">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="font-playfair text-2xl font-bold text-gold">85%+</p>
                      <p className="font-inter text-xs text-white/60 mt-0.5">Placement Rate</p>
                    </div>
                    <div>
                      <p className="font-playfair text-2xl font-bold text-gold">500+</p>
                      <p className="font-inter text-xs text-white/60 mt-0.5">Graduates</p>
                    </div>
                    <div>
                      <p className="font-playfair text-2xl font-bold text-gold">10+</p>
                      <p className="font-inter text-xs text-white/60 mt-0.5">Years Serving DFW</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
